import {
  ApiErrorState,
  Badge,
  Button,
  DestructiveConfirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  FilterBar,
  type FilterBarDef,
  type FilterBarValue,
  FormDrawer,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Switch,
  toast,
} from '@avila-roffe/components-web';
import {
  CheckCircle2,
  KeyRound,
  MailPlus,
  MoreHorizontal,
  Pencil,
  Search,
  Shield,
  Trash2,
  UserX,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { IdpUserCard } from '../components/IdpUserCard';
import { useIdpSearch } from '../hooks/useIdpSearch';
import { useTenantSettings } from '../TenantSettingsContext';
import type {
  DirectoryUser,
  TenantPermission,
  TenantRole,
  TenantUser,
} from '../TenantSettingsService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRoleLabel(roleName: string): string {
  return roleName
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

type BadgeVariant = 'primary' | 'secondary' | 'info' | 'destructive' | 'warning' | 'success';

function roleBadgeVariant(roleName: string): BadgeVariant {
  switch (roleName.toUpperCase()) {
    case 'SUPER_ADMIN':
      return 'destructive';
    case 'ADMIN':
      return 'warning';
    case 'VIEWER':
      return 'success';
    default:
      return 'secondary';
  }
}

function isSuperAdminRole(roleName: string): boolean {
  return roleName.toUpperCase() === 'SUPER_ADMIN';
}

function displayName(user: TenantUser): string {
  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }
  return user.name ?? user.email;
}

function initials(user: TenantUser): string {
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  if (first || last) return (first + last).toUpperCase();
  const fallback = user.name ?? user.email;
  return fallback
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function UserAvatar({ user }: { user: TenantUser }) {
  if (user.avatarUrl) {
    return (
      <img src={user.avatarUrl} alt="" className="size-10 shrink-0 rounded-full object-cover" />
    );
  }
  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground select-none">
      {initials(user)}
    </span>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function UsersSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-28" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: 5 }, (_, i) => `sk-${i}`).map((key) => (
          <div
            key={key}
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
          >
            <Skeleton className="size-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className="h-3 w-44" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="h-6 w-10 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Edit drawer ──────────────────────────────────────────────────────────────

function UserEditDrawer({
  user,
  allRoles,
  onClose,
  onUpdated,
}: {
  user: TenantUser;
  allRoles: TenantRole[];
  onClose: () => void;
  onUpdated: (updated: TenantUser) => void;
}) {
  const { service } = useTenantSettings();

  const [canLogin, setCanLogin] = React.useState(
    user.status !== 'suspended' && user.status !== 'blocked',
  );
  const [userRoles, setUserRoles] = React.useState<TenantRole[]>([]);
  const [permissions, setPermissions] = React.useState<TenantPermission[]>([]);
  const [loadingRoles, setLoadingRoles] = React.useState(true);
  const [loadingPerms, setLoadingPerms] = React.useState(false);
  const [pendingRoleId, setPendingRoleId] = React.useState<string | null>(null);
  const [isMutating, setIsMutating] = React.useState(false);

  const currentRoleId = userRoles[0]?.id ?? '';
  const selectedRoleId = pendingRoleId ?? currentRoleId;
  const isDirty = pendingRoleId !== null && pendingRoleId !== currentRoleId;

  React.useEffect(() => {
    setLoadingRoles(true);
    void service.getUserRoles(user.id).then((roles) => {
      setUserRoles(roles);
      setLoadingRoles(false);
    });
  }, [service, user.id]);

  React.useEffect(() => {
    if (!selectedRoleId) return;
    setLoadingPerms(true);
    void service.getRolePermissions(selectedRoleId).then((perms) => {
      setPermissions(perms);
      setLoadingPerms(false);
    });
  }, [service, selectedRoleId]);

  async function handleToggleLogin(checked: boolean) {
    setCanLogin(checked);
    setIsMutating(true);
    try {
      if (service.setAppAccess) {
        await service.setAppAccess(user.id, checked ? 'active' : 'blocked');
      } else {
        await service.updateUser(user.id, { status: checked ? 'active' : 'suspended' });
      }
      onUpdated({ ...user, status: checked ? 'active' : 'blocked' });
    } catch {
      setCanLogin(!checked);
      toast.error('Failed to update user access');
    } finally {
      setIsMutating(false);
    }
  }

  async function handleSubmit() {
    if (!isDirty) {
      onClose();
      return;
    }
    setIsMutating(true);
    try {
      if (currentRoleId) {
        await service.revokeRole(user.id, currentRoleId);
      }
      if (pendingRoleId) {
        await service.assignRole(user.id, pendingRoleId);
        const roleName = allRoles.find((r) => r.id === pendingRoleId)?.name ?? pendingRoleId;
        onUpdated({ ...user, roleId: pendingRoleId, roleName });
      }
      onClose();
    } catch {
      toast.error('Failed to update role');
    } finally {
      setIsMutating(false);
    }
  }

  return (
    <FormDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={displayName(user)}
      onSubmit={() => void handleSubmit()}
      submitLabel="Done"
      isPending={isMutating}
      submitDisabled={isMutating}
      isDirty={isDirty}
    >
      <p className="-mt-3 font-mono text-xs text-muted-foreground">{user.email}</p>

      {/* App access */}
      <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
        <div>
          <p className="text-sm font-medium">Access to this application</p>
          <p className="text-xs text-muted-foreground">
            {canLogin
              ? 'Account is active — user can authenticate'
              : 'Access blocked — user cannot log in to this application'}
          </p>
        </div>
        <Switch
          checked={canLogin}
          onCheckedChange={(v) => void handleToggleLogin(v)}
          disabled={isMutating}
          aria-label="Toggle login access"
        />
      </div>

      <div className="h-px bg-border" />

      {/* Role */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="size-4 text-muted-foreground" />
          <p className="text-sm font-medium">Role</p>
        </div>

        {loadingRoles ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <Select
            value={selectedRoleId}
            onValueChange={(v) => setPendingRoleId(v)}
            disabled={isMutating}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="No role assigned" />
            </SelectTrigger>
            <SelectContent>
              {allRoles.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {formatRoleLabel(r.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Permissions */}
        {selectedRoleId && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Permissions</p>
            {loadingPerms ? (
              <Skeleton className="h-4 w-full" />
            ) : permissions.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                No permissions assigned to this role.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {permissions.map((p) => (
                  <span
                    key={p.id}
                    className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground"
                  >
                    {p.resource}:{p.action}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </FormDrawer>
  );
}

// ─── Invite drawer ────────────────────────────────────────────────────────────

function InviteDrawer({
  open,
  onOpenChange,
  roles,
  defaultEmail,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: TenantRole[];
  defaultEmail?: string;
  onInvited: (u: TenantUser) => void;
}) {
  const { service } = useTenantSettings();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState(defaultEmail ?? '');
  const [roleId, setRoleId] = React.useState(roles[0]?.id ?? '');
  const [isPending, setIsPending] = React.useState(false);
  const [emailError, setEmailError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setEmail(defaultEmail ?? '');
      setRoleId(roles[0]?.id ?? '');
      setEmailError('');
    }
  }, [open, defaultEmail, roles]);

  async function handleSubmit() {
    setEmailError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address');
      return;
    }
    if (!roleId) return;
    setIsPending(true);
    try {
      const profile =
        firstName.trim() || lastName.trim()
          ? { firstName: firstName.trim() || undefined, lastName: lastName.trim() || undefined }
          : undefined;
      const user = await service.inviteUser(email, roleId, profile);
      onInvited(user);
      onOpenChange(false);
      toast.success('Invite sent');
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setIsPending(false);
    }
  }

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Invite a team member"
      onSubmit={() => void handleSubmit()}
      submitLabel="Send invite"
      isPending={isPending}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="inv-firstname">
              First name
            </label>
            <Input
              id="inv-firstname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Rafael"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="inv-lastname">
              Last name
            </label>
            <Input
              id="inv-lastname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Avila"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor="inv-email">
            Email address
          </label>
          <Input
            id="inv-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
          />
          {emailError && <p className="text-xs text-destructive">{emailError}</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor="inv-role">
            Role
          </label>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger id="inv-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {formatRoleLabel(r.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </FormDrawer>
  );
}

// ─── Reset password dialog ────────────────────────────────────────────────────

function ResetPasswordDialog({ user, onClose }: { user: TenantUser; onClose: () => void }) {
  const { service } = useTenantSettings();
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [isPending, setIsPending] = React.useState(false);
  const [error, setError] = React.useState('');

  async function handleSave() {
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setIsPending(true);
    try {
      await service.setTemporaryPassword?.(user.id, password);
      toast.success('Temporary password set');
      onClose();
    } catch {
      toast.error('Failed to set password');
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Set temporary password</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-muted-foreground">
            Setting a temporary password for{' '}
            <span className="font-medium text-foreground">{displayName(user)}</span>. They should
            change it on next login.
          </p>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="tmp-pwd">
              New password
            </label>
            <Input
              id="tmp-pwd"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="tmp-pwd-confirm">
              Confirm password
            </label>
            <Input
              id="tmp-pwd-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat password"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button appearance="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={isPending || !password || !confirm}
          >
            {isPending ? <Spinner className="size-4" /> : <KeyRound className="size-4" />}
            Set password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── User card ────────────────────────────────────────────────────────────────

function UserCard({
  user,
  onToggle,
  onRemove,
  onEdit,
  onApprove,
  onReject,
  onResetPassword,
  isToggling,
  isApproving,
  isRejecting,
}: {
  user: TenantUser;
  onToggle: () => void;
  onRemove: () => void;
  onEdit: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onResetPassword?: () => void;
  isToggling: boolean;
  isApproving?: boolean;
  isRejecting?: boolean;
}) {
  const isInvited = user.status === 'invited';
  const isSuperAdmin = isSuperAdminRole(user.roleName);

  return (
    <div className="flex w-full items-center gap-3 rounded-xl border bg-card p-3 transition-colors hover:bg-muted/40">
      {/* Clickable info area opens the edit drawer */}
      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
      >
        <UserAvatar user={user} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{displayName(user)}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{user.email}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {user.roleName && (
              <Badge variant={roleBadgeVariant(user.roleName)} appearance="soft" size="sm">
                {formatRoleLabel(user.roleName)}
              </Badge>
            )}
            {isInvited && (
              <Badge variant="warning" appearance="soft" size="sm">
                Pending
              </Badge>
            )}
            {user.status === 'suspended' && (
              <Badge variant="destructive" appearance="soft" size="sm">
                Suspended
              </Badge>
            )}
            {user.status === 'blocked' && (
              <Badge variant="warning" appearance="soft" size="sm">
                Blocked
              </Badge>
            )}
          </div>
        </div>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Actions"
            disabled={isToggling || !!isApproving || !!isRejecting}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            {isToggling || isApproving || isRejecting ? (
              <Spinner className="size-4" />
            ) : (
              <MoreHorizontal className="size-4" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isInvited && onApprove && (
            <DropdownMenuItem onClick={onApprove}>
              <CheckCircle2 className="size-4" /> Approve
            </DropdownMenuItem>
          )}
          {isInvited && onReject && (
            <DropdownMenuItem
              onClick={onReject}
              className="text-destructive focus:text-destructive"
            >
              <XCircle className="size-4" /> Reject
            </DropdownMenuItem>
          )}
          {isInvited && (onApprove || onReject) && <DropdownMenuSeparator />}

          {!isInvited && !isSuperAdmin && user.status === 'active' && (
            <>
              <DropdownMenuItem onClick={onToggle}>
                <XCircle className="size-4" /> Suspend
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {!isInvited && !isSuperAdmin && user.status === 'suspended' && (
            <>
              <DropdownMenuItem onClick={onToggle}>
                <CheckCircle2 className="size-4" /> Reactivate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onSelect={() => setTimeout(onEdit, 80)}>
            <Pencil className="size-4" /> Edit
          </DropdownMenuItem>
          {onResetPassword && (
            <DropdownMenuItem onSelect={() => setTimeout(onResetPassword, 80)}>
              <KeyRound className="size-4" /> Reset password
            </DropdownMenuItem>
          )}
          {!isSuperAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onRemove}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" /> Remove
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ─── Filter defs ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', icon: <CheckCircle2 className="size-3.5 text-success" /> },
  { value: 'invited', label: 'Pending', icon: <MailPlus className="size-3.5 text-warning" /> },
  {
    value: 'suspended',
    label: 'Suspended',
    icon: <XCircle className="size-3.5 text-destructive" />,
  },
  {
    value: 'blocked',
    label: 'Blocked',
    icon: <UserX className="size-3.5 text-warning" />,
  },
];

const USER_FILTER_DEFS: FilterBarDef[] = [
  { key: 'name', label: 'Name', type: 'text', icon: <Search className="size-3.5" /> },
  { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
];

const IDP_STATUS_FILTER_DEFS: FilterBarDef[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      ...STATUS_OPTIONS,
      {
        value: 'no_access',
        label: 'No access',
        icon: <UserX className="size-3.5 text-muted-foreground" />,
      },
    ],
  },
];

// ─── UsersTab ─────────────────────────────────────────────────────────────────

export function UsersTab() {
  const { service } = useTenantSettings();
  const hasIdp = !!service.searchDirectory;

  const [users, setUsers] = React.useState<TenantUser[]>([]);
  const [roles, setRoles] = React.useState<TenantRole[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = React.useState<string | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState<string | null>(null);
  const [approvingId, setApprovingId] = React.useState<string | null>(null);
  const [rejectingId, setRejectingId] = React.useState<string | null>(null);
  const [editUser, setEditUser] = React.useState<TenantUser | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = React.useState<TenantUser | null>(null);
  const [filters, setFilters] = React.useState<FilterBarValue[]>([]);
  const [idpSearchQuery, setIdpSearchQuery] = React.useState('');
  const [pendingEmails, setPendingEmails] = React.useState<Set<string>>(new Set());
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');

  const searchQuery = hasIdp
    ? idpSearchQuery
    : (filters.find((f) => f.key === 'name')?.value ?? '');
  const statusFilter = filters.find((f) => f.key === 'status')?.value ?? '';

  // IDP directory search — always passes searchQuery so initial empty-query load runs on mount
  const {
    results: dirResults,
    idpOnlyUsers,
    searching: idpSearching,
  } = useIdpSearch(service, searchQuery, users);

  // Merged data for IDP mode
  const platformEmailMap = React.useMemo(
    () => new Map(users.filter((u) => u.email).map((u) => [u.email.toLowerCase(), u])),
    [users],
  );
  const dirEmailSet = React.useMemo(
    () => new Set(dirResults.filter((d) => d.email).map((d) => d.email.toLowerCase())),
    [dirResults],
  );
  const platformOnlyUsers = React.useMemo(
    () => users.filter((u) => u.email && !dirEmailSet.has(u.email.toLowerCase())),
    [users, dirEmailSet],
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, r] = await Promise.all([service.listUsers(), service.listAppRoles()]);
      setRoles(r);
      setUsers(u);
    } catch {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Standard mode: filter platform users by search + status
  const filteredUsers = React.useMemo(() => {
    return users.filter((u) => {
      const q = searchQuery.toLowerCase();
      if (q) {
        const name = displayName(u).toLowerCase();
        if (!u.email?.toLowerCase().includes(q) && !name.includes(q)) return false;
      }
      if (statusFilter && u.status !== statusFilter) return false;
      return true;
    });
  }, [users, searchQuery, statusFilter]);

  // IDP mode: filter merged results by status
  const filteredDirResults = React.useMemo(() => {
    if (!statusFilter) return dirResults;
    if (statusFilter === 'no_access') {
      return dirResults.filter((d) => !platformEmailMap.has(d.email.toLowerCase()));
    }
    return dirResults.filter((d) => {
      const pu = platformEmailMap.get(d.email.toLowerCase());
      return !!pu && pu.status === statusFilter;
    });
  }, [dirResults, platformEmailMap, statusFilter]);

  const filteredPlatformOnly = React.useMemo(() => {
    return platformOnlyUsers.filter((u) => {
      if (idpSearchQuery) {
        const q = idpSearchQuery.toLowerCase();
        if (!u.email?.toLowerCase().includes(q) && !displayName(u).toLowerCase().includes(q))
          return false;
      }
      if (statusFilter && statusFilter !== 'no_access' && u.status !== statusFilter) return false;
      return true;
    });
  }, [platformOnlyUsers, idpSearchQuery, statusFilter]);

  function openInviteWith(email: string) {
    setInviteEmail(email);
    setInviteOpen(true);
  }

  async function handleAdd(du: DirectoryUser) {
    if (!service.addUser) return;
    const email = du.email;
    setPendingEmails((prev) => new Set(prev).add(email));
    try {
      const added = await service.addUser(email, { firstName: du.givenName, lastName: du.surname });
      setUsers((prev) => [...prev, added]);
      toast.success('User added');
    } catch {
      toast.error('Failed to add user');
    } finally {
      setPendingEmails((prev) => {
        const s = new Set(prev);
        s.delete(email);
        return s;
      });
    }
  }

  async function approve(user: TenantUser) {
    if (!service.approveUser) return;
    setApprovingId(user.id);
    try {
      const updated = await service.approveUser(user.id);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      toast.success('User approved');
    } catch {
      toast.error('Failed to approve user');
    } finally {
      setApprovingId(null);
    }
  }

  async function reject(user: TenantUser) {
    if (!service.rejectUser) return;
    setRejectingId(user.id);
    try {
      await service.rejectUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('User rejected');
    } catch {
      toast.error('Failed to reject user');
    } finally {
      setRejectingId(null);
    }
  }

  async function toggleSuspend(user: TenantUser) {
    setTogglingId(user.id);
    try {
      const updated = await service.updateUser(user.id, {
        status: user.status === 'suspended' ? 'active' : 'suspended',
      });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      toast.success(updated.status === 'suspended' ? 'User suspended' : 'User reactivated');
    } catch {
      toast.error('Failed to update user');
    } finally {
      setTogglingId(null);
    }
  }

  async function confirmRemove() {
    if (!pendingRemoveId) return;
    setIsRemoving(true);
    try {
      await service.removeUser(pendingRemoveId);
      setUsers((prev) => prev.filter((u) => u.id !== pendingRemoveId));
      toast.success('User removed');
    } catch {
      toast.error('Failed to remove user');
    } finally {
      setIsRemoving(false);
      setPendingRemoveId(null);
    }
  }

  if (loading) return <UsersSkeleton />;
  if (error) return <ApiErrorState message={error} onRetry={() => void load()} className="mt-4" />;

  const removeTarget = users.find((u) => u.id === pendingRemoveId);
  const removeTargetLabel = removeTarget ? displayName(removeTarget) : 'this user';

  // Shared modals rendered in both modes
  const modals = (
    <>
      {editUser && (
        <UserEditDrawer
          user={editUser}
          allRoles={roles}
          onClose={() => setEditUser(null)}
          onUpdated={(updated) => {
            setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
            setEditUser(updated);
          }}
        />
      )}
      <InviteDrawer
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={roles}
        defaultEmail={inviteEmail}
        onInvited={(u) => setUsers((prev) => [...prev, u])}
      />
      <DestructiveConfirm
        open={pendingRemoveId !== null}
        onOpenChange={(o) => {
          if (!o) setPendingRemoveId(null);
        }}
        title="Remove member"
        description={`Remove ${removeTargetLabel} from the tenant? They will lose access immediately.`}
        confirmLabel="Remove"
        onConfirm={() => void confirmRemove()}
        isPending={isRemoving}
      />
      {resetPasswordUser && (
        <ResetPasswordDialog user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />
      )}
    </>
  );

  // Reusable UserCard render (key set at call site)
  const userCardProps = (user: TenantUser) => ({
    user,
    isToggling: togglingId === user.id,
    isApproving: approvingId === user.id,
    isRejecting: rejectingId === user.id,
    onToggle: () => void toggleSuspend(user),
    onRemove: () => setPendingRemoveId(user.id),
    onEdit: () => setEditUser(user),
    onApprove: service.approveUser ? () => void approve(user) : undefined,
    onReject: service.rejectUser ? () => void reject(user) : undefined,
    onResetPassword: service.setTemporaryPassword ? () => setResetPasswordUser(user) : undefined,
  });

  // ── IDP mode: search bar + merged directory/platform grid ─────────────────
  if (hasIdp) {
    const initialLoading = idpSearching && dirResults.length === 0;
    const totalVisible = filteredDirResults.length + filteredPlatformOnly.length;

    return (
      <>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {users.length} member{users.length !== 1 ? 's' : ''}
            </p>
            <Button
              variant="primary"
              size="sm"
              className="gap-1.5"
              onClick={() => openInviteWith('')}
            >
              <MailPlus className="size-4" />
              Invite user
            </Button>
          </div>

          <FilterBar
            defs={IDP_STATUS_FILTER_DEFS}
            value={filters}
            onChange={setFilters}
            layout="split"
            leading={
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search directory…"
                  value={idpSearchQuery}
                  onChange={(e) => setIdpSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {idpSearching && (
                  <Spinner className="absolute right-3 top-1/2 -translate-y-1/2 size-4" />
                )}
              </div>
            }
          />

          {initialLoading ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={i} className="flex items-center gap-3 rounded-xl border bg-card p-3">
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : totalVisible === 0 ? (
            idpSearchQuery ? (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed py-10 text-center">
                <p className="text-sm text-muted-foreground">
                  No users found for &ldquo;{idpSearchQuery}&rdquo;
                </p>
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => openInviteWith(idpSearchQuery)}
                >
                  <MailPlus className="size-4" />
                  Invite this person
                </Button>
              </div>
            ) : (
              <EmptyState
                variant={filters.length > 0 ? 'noResults' : 'noData'}
                heading={
                  filters.length > 0
                    ? 'No users match the current filters.'
                    : 'No users in this directory.'
                }
              />
            )
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {filteredDirResults.map((d) => {
                const pu = platformEmailMap.get(d.email.toLowerCase());
                if (pu) {
                  return <UserCard key={pu.id} {...userCardProps(pu)} />;
                }
                return (
                  <IdpUserCard
                    key={d.externalId || d.email}
                    user={d}
                    onInvite={() => openInviteWith(d.email)}
                    onAdd={service.addUser ? () => void handleAdd(d) : undefined}
                    isPending={pendingEmails.has(d.email)}
                  />
                );
              })}
              {filteredPlatformOnly.map((u) => (
                <UserCard key={u.id} {...userCardProps(u)} />
              ))}
            </div>
          )}
        </div>
        {modals}
      </>
    );
  }

  // ── Standard mode: list ───────────────────────────────────────────────────
  const hasResults = filteredUsers.length > 0 || idpOnlyUsers.length > 0;
  const isFiltering = filters.length > 0;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {users.length} member{users.length !== 1 ? 's' : ''}
          </p>
          <Button
            variant="primary"
            size="sm"
            className="gap-1.5"
            onClick={() => openInviteWith('')}
          >
            <MailPlus className="size-4" aria-hidden="true" />
            Invite user
          </Button>
        </div>

        <FilterBar defs={USER_FILTER_DEFS} value={filters} onChange={setFilters} />

        {!hasResults ? (
          idpSearching ? (
            <div className="flex items-center justify-center gap-2 py-10 text-xs text-muted-foreground">
              <Spinner className="size-3" />
              Loading directory…
            </div>
          ) : isFiltering ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed py-10 text-center">
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? `No users found for "${searchQuery}"`
                  : 'No users match your filters.'}
              </p>
              {searchQuery && (
                <Button size="sm" className="gap-1.5" onClick={() => openInviteWith(searchQuery)}>
                  <MailPlus className="size-4" />
                  Invite this person
                </Button>
              )}
            </div>
          ) : (
            <EmptyState variant="noData" heading="No members yet. Invite someone to get started." />
          )
        ) : (
          <div className="space-y-2">
            {filteredUsers.map((user) => (
              <UserCard key={user.id} {...userCardProps(user)} />
            ))}
            {idpOnlyUsers.length > 0 && (
              <>
                {filteredUsers.length > 0 && (
                  <p className="pt-2 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    In your directory
                  </p>
                )}
                {idpOnlyUsers.map((du) => (
                  <IdpUserCard
                    key={du.externalId || du.email}
                    user={du}
                    onInvite={() => openInviteWith(du.email)}
                    onAdd={service.addUser ? () => void handleAdd(du) : undefined}
                    isPending={pendingEmails.has(du.email)}
                  />
                ))}
              </>
            )}
            {idpSearching && (
              <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
                <Spinner className="size-3" />
                Searching directory…
              </div>
            )}
          </div>
        )}
      </div>
      {modals}
    </>
  );
}
